import { addQueryParamsToUrl } from '@healthhub/react-ui';
import { generatePath } from 'react-router';

import { getSDCFormAppointment, getSHCFormAppointment } from '@shared/configs/configs/config';
import { Cluster } from '@shared/constants/constants';
import { APPOINTMENT_REQUEST_FORM_PATH } from '@shared/constants/paths';
import { SERVICE_CATALOG_CODE } from '@shared/constants/serviceCatalog';
import { LayoutViewBridge } from '@shared/context';

import type { RequestFormTypeType } from '../AppointmentMakeNew/types/makeNew';
import {
  COMMENT_PHONE_NUMBER_REGEX,
  CONTACT_LINK,
  DESCRIPTION_TEXT,
  FORM_PLACEHOLDERS,
  MIX_TEXT_AND_PHONE_NUMBER_REGEX,
  NHG_REQUEST_FORM_ANCHOR_REGEX,
  REQUEST_FORM_BOOK_NEXT_TEXT,
  REQUEST_FORM_TEXT,
} from '../constants';

interface FormatCommentParams {
  formType: RequestFormTypeType;
  mid?: null | string;
  responseString: string;
  submitLink: string;
}

interface FormatRequestFormProps {
  clusterCode?: null | string;
  formType: RequestFormTypeType;
  isLiveChatEnabled?: boolean;
  isRemoveContactNumber?: boolean;
  isUpcomingAppointment?: boolean;
  mid?: null | string;
  responseString?: null | string;
  skipNHGClusterHandling?: boolean;
}

function applyContactNumberFormatting(comment: string, isRemoveContactNumber: boolean): string {
  if (isRemoveContactNumber) {
    return comment.replace(MIX_TEXT_AND_PHONE_NUMBER_REGEX, '').replace(COMMENT_PHONE_NUMBER_REGEX, '');
  }

  return comment.replace(FORM_PLACEHOLDERS.CONTACT, `${CONTACT_LINK}?action=externallink`);
}

function createSubmitLink(formType: RequestFormTypeType, mid?: null | string): string {
  return `<a href="${addQueryParamsToUrl(generatePath(APPOINTMENT_REQUEST_FORM_PATH, { formType }), {
    mid: mid ?? '',
  })}" class="link">submit a request online</a>`;
}

function formatComment({ formType, mid, responseString, submitLink }: FormatCommentParams): string {
  const formPlaceholders = Object.values(FORM_PLACEHOLDERS);
  const hasFormPlaceholder = formPlaceholders.some((placeholder) => responseString.includes(placeholder));

  if (!hasFormPlaceholder) {
    return responseString
      .replace(REQUEST_FORM_TEXT, submitLink)
      .replace(DESCRIPTION_TEXT, `<p class="mb-[-8px]">${DESCRIPTION_TEXT}</p>`);
  }

  const urlWithViewPath = addQueryParamsToUrl(generatePath(APPOINTMENT_REQUEST_FORM_PATH, { formType }), {
    mid: mid ?? '',
  });

  return responseString
    .replace(FORM_PLACEHOLDERS.REQUEST_FORM, urlWithViewPath)
    .replace(FORM_PLACEHOLDERS.SUBMIT_REQUEST, urlWithViewPath)
    .replace(FORM_PLACEHOLDERS.SDC_FORM, getSDCFormAppointment())
    .replace(FORM_PLACEHOLDERS.SHC_FORM, getSHCFormAppointment())
    .replace(DESCRIPTION_TEXT, `<p class="mb-[-8px]">${DESCRIPTION_TEXT}</p>`);
}

function handleNHGClusterResponse(responseString: string): string {
  if (NHG_REQUEST_FORM_ANCHOR_REGEX.test(responseString)) {
    return responseString.replace(NHG_REQUEST_FORM_ANCHOR_REGEX, REQUEST_FORM_BOOK_NEXT_TEXT);
  }
  return responseString.replace(REQUEST_FORM_TEXT, REQUEST_FORM_BOOK_NEXT_TEXT);
}

const handleNUHSClusterResponse = (responseString: string): string => {
  const livechatAttr = `href="" data-navigation=${SERVICE_CATALOG_CODE.livechat}`;
  const placeholders = [FORM_PLACEHOLDERS.REQUEST_FORM, FORM_PLACEHOLDERS.SUBMIT_REQUEST];

  return placeholders.reduce(
    (result, placeholder) =>
      result.replace(new RegExp(`href=['"]${placeholder.replace(/[[\]]/g, '\\$&')}['"]`), livechatAttr),
    responseString
  );
};

export const formatRequestForm = ({
  clusterCode,
  formType,
  isRemoveContactNumber = false,
  isUpcomingAppointment = false,
  isLiveChatEnabled = false,
  mid,
  responseString,
  skipNHGClusterHandling = false,
}: FormatRequestFormProps): string => {
  if (!responseString) return '';

  // Handle NHG cluster specific logic
  if (clusterCode === Cluster.NHG && !isUpcomingAppointment && !skipNHGClusterHandling) {
    return handleNHGClusterResponse(responseString);
  }

  // Handle NUHS cluster specific logic
  const { isMobileView } = LayoutViewBridge.getLayoutViewBridge?.() ?? {};
  if (clusterCode === Cluster.NUHS && isLiveChatEnabled && isMobileView) {
    return handleNUHSClusterResponse(responseString);
  }

  const submitLink = createSubmitLink(formType, mid);
  const formattedComment = formatComment({ responseString, submitLink, formType, mid });

  // Apply contact number handling
  return applyContactNumberFormatting(formattedComment, isRemoveContactNumber);
};
